import { useEffect, useState } from "react";
import { ClipboardList, Loader2, Plus, Check } from "lucide-react";
import { adminApi } from "../lib/adminAuth";

const RANGES = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "all", label: "All time" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const prettySource = (s) => {
  if (!s) return "—";
  if (s === "hero_lookup") return "Hero lookup";
  try {
    const u = new URL(s);
    return (u.pathname && u.pathname !== "/") ? u.pathname : (u.hostname || s);
  } catch {
    return s;
  }
};

/**
 * MedicationRequestsWidget — compact admin view of the most-requested
 * UNSUPPORTED medications, derived only from real medication_lookup_not_found
 * events. Self-fetches lazily so it never blocks the dashboard. Includes a safe
 * "Add to coverage backlog" action (writes to a separate collection).
 */
export default function MedicationRequestsWidget() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(null); // normalized currently being added

  const load = (r) => {
    setLoading(true);
    setError("");
    return adminApi()
      .get(`/events/medication-requests?range=${r}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi()
      .get(`/events/medication-requests?range=${range}`)
      .then(({ data }) => { if (!cancelled) { setData(data); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const addToBacklog = async (row) => {
    setAdding(row.normalized);
    try {
      await adminApi().post("/events/medication-requests/backlog", {
        term: row.term, normalized: row.normalized,
      });
      setData((d) => d && {
        ...d,
        rows: d.rows.map((r) =>
          r.normalized === row.normalized ? { ...r, in_backlog: true } : r),
      });
    } catch {
      /* swallow — non-critical admin action */
    } finally {
      setAdding(null);
    }
  };

  return (
    <section data-testid="med-requests-widget" className="mt-12">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500">
          <ClipboardList className="h-3.5 w-3.5 text-stone-600" />
          Top requested medications (not yet covered)
        </h2>
        <div className="inline-flex rounded-lg border border-stone-200 bg-white p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`med-requests-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                range === r.key ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-900"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-stone-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading requests…</span>
          </div>
        ) : error ? (
          <p data-testid="med-requests-error" className="py-4 text-sm text-red-600">
            Failed to load: {error}
          </p>
        ) : !data?.has_data ? (
          <p data-testid="med-requests-empty" className="py-8 text-center text-sm font-medium text-stone-600">
            No medication requests yet.
          </p>
        ) : (
          <>
            <p className="mb-3 text-[11px] text-stone-400">
              {data.total_requests} request{data.total_requests === 1 ? "" : "s"} across {data.unique_terms} unique term{data.unique_terms === 1 ? "" : "s"}.
            </p>
            <div className="overflow-x-auto">
              <table data-testid="med-requests-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                    <th className="py-2 pr-3 font-medium">Medication / term</th>
                    <th className="px-3 py-2 text-right font-medium">Count</th>
                    <th className="px-3 py-2 text-right font-medium">First</th>
                    <th className="px-3 py-2 text-right font-medium">Last</th>
                    <th className="px-3 py-2 font-medium">Source</th>
                    <th className="py-2 pl-3 text-right font-medium">Backlog</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr
                      key={row.normalized}
                      data-testid={`med-requests-row-${row.normalized}`}
                      className="border-b border-stone-100 last:border-0"
                    >
                      <td className="py-2.5 pr-3 font-medium capitalize text-stone-800">{row.term}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-stone-700">{row.count}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-stone-500">{fmtDate(row.first_requested)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-stone-500">{fmtDate(row.last_requested)}</td>
                      <td className="px-3 py-2.5 text-stone-500 truncate max-w-[140px]">{prettySource(row.latest_source)}</td>
                      <td className="py-2.5 pl-3 text-right">
                        {row.in_backlog ? (
                          <span
                            data-testid={`med-requests-backlogged-${row.normalized}`}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700"
                          >
                            <Check className="h-3.5 w-3.5" /> Added
                          </span>
                        ) : (
                          <button
                            type="button"
                            data-testid={`med-requests-add-${row.normalized}`}
                            onClick={() => addToBacklog(row)}
                            disabled={adding === row.normalized}
                            className="inline-flex items-center gap-1 rounded-md border border-stone-200 px-2 py-1 text-[11px] font-medium text-stone-600 transition hover:border-stone-900 hover:text-stone-900 disabled:opacity-50"
                          >
                            {adding === row.normalized ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                            Backlog
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
